import React, { useMemo, useState } from 'react';

import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LayersIcon from '@mui/icons-material/Layers';
import { Autocomplete, Box, IconButton, InputAdornment, Menu, MenuItem, Stack, TextField, Tooltip, Typography, alpha } from '@mui/material';

import { ChangeSetViewModel } from '../../api/api';
import { formatTimestampRelative } from '../../common';
import { useChangeSet } from '../ChangeSetContext';

interface MenuItemChangeSetSelectorProps {
	isDrawerOpen: boolean;
}

const MenuItemChangeSetSelector: React.FC<MenuItemChangeSetSelectorProps> = ({ isDrawerOpen }) => {
	const { currentChangeSetId, setCurrentChangeSetId, availableChangeSets } = useChangeSet();
	const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);

	// Find the currently selected changeSet object
	const selectedChangeSet = useMemo(() => availableChangeSets.find((cs) => cs.id === currentChangeSetId) ?? null, [availableChangeSets, currentChangeSetId]);

	const handleChangeSetChange = (_event: React.SyntheticEvent, value: ChangeSetViewModel | null) => {
		setCurrentChangeSetId(value?.id ?? null);
	};
	const selectChangeSet = (changeSet: ChangeSetViewModel) => {
		setCurrentChangeSetId(changeSet.id);
		setMenuAnchor(null);
	};
	const isActive = selectedChangeSet?.status === 'Active';

	if (isDrawerOpen) {
		// Don't render Autocomplete until we have both changeSets and a valid selection
		if (availableChangeSets.length === 0 || !selectedChangeSet) {
			return null;
		}

		return (
			<Stack spacing={0.5}>
				<Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 0.5 }}>
					<Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
						Change set
					</Typography>
					<Stack direction="row" spacing={0.5} alignItems="center">
						<Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: isActive ? 'success.main' : 'text.secondary' }} />
						<Typography variant="caption" color="text.secondary">{selectedChangeSet.status}</Typography>
					</Stack>
				</Stack>
				<Autocomplete
					size="small"
					options={availableChangeSets}
					getOptionLabel={(option) => option.name ?? ''}
					value={selectedChangeSet}
					onChange={handleChangeSetChange}
					isOptionEqualToValue={(option, value) => option.id === value.id}
					disableClearable
					renderOption={(props, option) => (
						<Box component="li" {...props} key={option.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
							<LayersIcon fontSize="small" color="primary" />
							<Stack sx={{ minWidth: 0, flex: 1 }}>
								<Typography variant="body2" noWrap>{option.name}</Typography>
								<Typography variant="caption" color="text.secondary">{option.updatedDate ? `Updated ${formatTimestampRelative(option.updatedDate)}` : option.status}</Typography>
							</Stack>
							<Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: option.status === 'Active' ? 'success.main' : 'text.secondary' }} />
						</Box>
					)}
					renderInput={(params) => (
						<TextField
							{...params}
							inputProps={{ ...params.inputProps, 'aria-label': 'Selected change set' }}
							InputProps={{
								...params.InputProps,
								startAdornment: <InputAdornment position="start"><LayersIcon fontSize="small" /></InputAdornment>,
							}}
							sx={{
								'& .MuiOutlinedInput-root': {
									backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.07),
									'&:hover': { backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.16 : 0.11) },
								},
								'& .MuiInputBase-input': { fontSize: '13px', fontWeight: 500 },
							}}
						/>
					)}
					fullWidth
				/>
			</Stack>
		);
	}

	// Compact view when drawer is closed - show icon
	return (
		<>
		<Tooltip title={selectedChangeSet ? `Change set: ${selectedChangeSet.name}` : 'Select change set'} placement="right">
			<IconButton
				size="small"
				aria-label="Select change set"
				onClick={(event) => setMenuAnchor(event.currentTarget)}
				sx={{
					mx: 'auto',
					display: 'flex',
					color: 'primary.main',
					backgroundColor: (theme) => alpha(theme.palette.primary.main, theme.palette.mode === 'dark' ? 0.1 : 0.08),
				}}
			>
				<LayersIcon fontSize="small" />
			</IconButton>
		</Tooltip>
		<Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)} anchorOrigin={{ vertical: 'center', horizontal: 'right' }} transformOrigin={{ vertical: 'center', horizontal: 'left' }}>
			{availableChangeSets.map((changeSet) => (
				<MenuItem key={changeSet.id} selected={changeSet.id === currentChangeSetId} onClick={() => selectChangeSet(changeSet)} sx={{ minWidth: 240, gap: 1 }}>
					<LayersIcon fontSize="small" color="primary" />
					<Stack sx={{ minWidth: 0, flex: 1 }}>
						<Typography variant="body2" noWrap>{changeSet.name}</Typography>
						<Typography variant="caption" color="text.secondary">{changeSet.status}{changeSet.updatedDate ? ` · ${formatTimestampRelative(changeSet.updatedDate)}` : ''}</Typography>
					</Stack>
					{changeSet.id === currentChangeSetId ? <CheckIcon fontSize="small" color="primary" /> : <ExpandMoreIcon fontSize="small" sx={{ opacity: 0 }} />}
				</MenuItem>
			))}
		</Menu>
		</>
	);
};

export default MenuItemChangeSetSelector;
